import type { CreateIncidentStepId } from '../CreateIncident/createIncidentModel';
import type { InitialResponseTabsType } from 'components/IAP/initialResponsePage/InitialResponseTabs';

/**
 * The tour runs outside React, so each host route registers the handful of
 * things it alone can do. Everything is optional: a phase whose host is not
 * mounted simply skips that behaviour.
 */
export type IncidentLifecycleTourApi = {
  /** Hub: open + Create so the incident item can be spotlighted. */
  openCreateMenu: () => void;
  /** Wizard: jump between steps without submitting the form. */
  setWizardStep: (step: CreateIncidentStepId) => void;
  /** Wizard: write the demo exercise name and type onto the form. */
  applyTourIncidentDefaults: () => void;
  /**
   * Wizard: submit the prefilled form. Returns false when create is still
   * blocked so the tour can fall back to an existing workspace.
   */
  submitTourIncident: () => boolean;
  /**
   * Workspace: hide the map and show the IR stepper. The walkthrough targets
   * live on that surface, and a new incident opens with the map / forms view.
   */
  prepareWorkspaceSurface: () => void;
  /** Workspace: move the initial response stepper. */
  setWorkspacePhase: (phase: InitialResponseTabsType) => void;
  /** Workspace: Legacy stepper vs the ICS-201 forms view. */
  setWorkspaceView: (view: 'legacy' | 'new') => void;
  /** Planning P: move the 8-phase stepper (0-based index). */
  setPlanningPhase: (index: number) => void;
  /** Planning P: pick the form card rendered under the Forms dropdown. */
  setPlanningCard: (widgetName: string) => void;
  /** Continue the tour on another route. */
  navigateTo: (href: string) => void;
  /** Workspace to walk after the wizard, or null if the org has none. */
  resolveWorkspaceHref: () => string | null;
  /**
   * Planning P dashboard of an escalated incident, or null if none exists —
   * Planning P only opens once an incident has escalated.
   */
  resolvePlanningHref: () => string | null;
  /** Settings route for the incident currently being walked. */
  resolveArchiveHref: () => string | null;
  /**
   * Escalate this workspace and return its Planning P tour href, or null if
   * escalate failed.
   */
  escalateCurrentWorkspace: () => Promise<string | null>;
};

let api: Partial<IncidentLifecycleTourApi> = {};

export function registerIncidentLifecycleTourApi(
  partial: Partial<IncidentLifecycleTourApi>
): () => void {
  api = { ...api, ...partial };
  return () => {
    for (const key of Object.keys(
      partial
    ) as (keyof IncidentLifecycleTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getIncidentLifecycleTourApi(): Partial<IncidentLifecycleTourApi> {
  return api;
}
